import Joi from "joi";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import Toast from "../../helpers/Toast";
import { PartDTO } from "../../models/Parts/PartDTO";
import { PartUpdateDTO } from "../../models/Parts/PartUpdateDTO";
import { PartService } from "../../services/PartService";
import useValidationSchema, { FieldConfig } from "../../components/Validations";

export default function PartEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [part, setPart] = useState<PartDTO>({} as PartDTO);

    const partService = new PartService();
    const navigate = useNavigate();

    const getPart = async () => {
        var response = await partService.GetByIdErp(id);
        if (response.success === false || response.obj == null) {
            Toast.Show("error", response.message);
            return;
        }

        setPart({
            erpId: response.obj.erpId,
            name: response.obj.name,
            sellingPrice: response.obj.sellingPrice,
            costPrice: response.obj.costPrice
        });

        setLoadingUpdate(false);
    };

    useEffect(() => {   
        getPart();
    }, []);

    const fields: FieldConfig[] = [
        ["erpId", "string", "Screens.Parts.Edit.Validation.CodeRequired"],
        ["name", "string", "Screens.Parts.Edit.Validation.NameRequired"],
        ["sellingPrice", "price", "Screens.Parts.Edit.Validation.SellingPriceRequired"],
        ["costPrice", "price", "Screens.Parts.Edit.Validation.CostPriceRequired"]
    ];
    const schemaClientValidation = useValidationSchema(fields);

    const editPart = async () => {
        setLoadingUpdate(true);

        const updatedPart: PartUpdateDTO = {
            erpId: part.erpId || id,
            name: part.name,
            sellingPrice: part.sellingPrice,
            costPrice: part.costPrice
        };

        const { error } = schemaClientValidation.validate(updatedPart);
        if (error) {
            Toast.Show("error", error.details.map(detail => detail.message).join(', '));
            setLoadingUpdate(false);
            return;
        }

        const response = await partService.Edit(updatedPart);
        setLoadingUpdate(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }
        navigate(-1);
        Toast.Show("success", response.message);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle title={"Editar Peça"} allowGoBack={true} />
                </div>
                <div className="pageBody">
                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={2}
                                md={2}
                                sm={2}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={part.erpId}
                                label={intl.formatMessage({ id: "Screens.Parts.Info.ErpId" })}
                                readOnly={false}
                                onChange={(t: string) => {
                                    setPart({ ...part, erpId: t });
                                }}
                            />
                            <InputComponent
                                xl={3}
                                lg={3}
                                md={3}
                                sm={3}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={part.name}
                                label={intl.formatMessage({ id: "Screens.Parts.Info.Name" })}
                                readOnly={false}
                                onChange={(t: string) => {
                                    setPart({ ...part, name: t });
                                }}
                            />
                        </Row>

                        <Row>
                            <InputComponent
                                xl={1}
                                lg={1}
                                md={1}
                                sm={1}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"number"}
                                value={part.sellingPrice}
                                label={intl.formatMessage({ id: "Screens.Parts.Info.SellingPrice" })}
                                readOnly={false}
                                onChange={(t: number) => {
                                    setPart({ ...part, sellingPrice: t });
                                }}
                            />
                            <InputComponent
                                xl={1}
                                lg={1}
                                md={1}
                                sm={1}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"number"}
                                value={part.costPrice}
                                label={intl.formatMessage({ id: "Screens.Parts.Info.CostPrice" })}
                                readOnly={false}
                                onChange={(t: number) => {
                                    setPart({ ...part, costPrice: t });
                                }}
                            />
                        </Row>

                        <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
                            <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                                <ButtonComponent
                                    disabled={loadingUpdate}
                                    loading={loadingUpdate}
                                    text={intl.formatMessage({ id: "Screens.Parts.Create.Save" })}
                                    onClick={editPart}
                                />
                            </Col>
                        </Row>
                    </Row>
                </div>
            </div>
        </Layout>
    );
}
